import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Flame, Activity, Wheat, Droplet, ChevronLeft, ChevronRight, Upload, Sparkles, Loader2, Trash2, CheckSquare, Square, RefreshCw, Search, BookOpen, ImagePlus } from 'lucide-react';
import Navbar from '@/components/Navbar';
import ContentCreator from '@/components/ContentCreator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available tiers for main meals
const MAIN_MEAL_TIERS = [400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400];

export default function TierRecipeLibrary({ user, onLogout }) {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('all'); // Changed to 'all' to show all recipes by default
  const [selectedRecipe, setSelectedRecipe] = useState(null); // For detail view
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const recipesPerPage = 12; // Reduced from 24 to 12 for faster generation
  
  // New filters
  const [mealTypeFilter, setMealTypeFilter] = useState('all');
  const [dietTypeFilter, setDietTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate state for input to debounce
  
  // Delete functionality
  const [selectedRecipeIds, setSelectedRecipeIds] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Import CSV
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  
  // Generate Images
  const [generatingImages, setGeneratingImages] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [regeneratingImageId, setRegeneratingImageId] = useState(null);
  
  // CSV Format Helper
  const [csvFormatDialogOpen, setCsvFormatDialogOpen] = useState(false);
  
  // Edit mode for moving ingredients
  const [editMode, setEditMode] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  
  // Recipe list view
  const [listViewOpen, setListViewOpen] = useState(false);

  // Tab: 'library' | 'content'
  const [activeTab, setActiveTab] = useState('library');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1); // Reset to first page on search
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchRecipes();
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [selectedTier, mealTypeFilter, dietTypeFilter, searchQuery]);

  // Keyboard navigation for recipe modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedRecipe) return;
      
      if (e.key === 'ArrowLeft') {
        navigateToPrevRecipe();
      } else if (e.key === 'ArrowRight') {
        navigateToNextRecipe();
      } else if (e.key === 'Escape') {
        closeRecipeDetail();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedRecipe, recipes]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      if (selectedTier !== 'all') {
        params.append('tier', selectedTier);
      }
      if (mealTypeFilter !== 'all') {
        params.append('meal_type', mealTypeFilter);
      }
      if (dietTypeFilter !== 'all') {
        params.append('diet_type', dietTypeFilter);
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      console.log('[RecipeLibrary] Fetching with params:', params.toString());
      
      const response = await axios.get(`${API}/tier-recipes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[RecipeLibrary] Received', response.data.length, 'recipes');
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
      toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  const openRecipeDetail = (recipe) => {
    setSelectedRecipe(recipe);
  };

  const closeRecipeDetail = () => {
    setSelectedRecipe(null);
    setEditMode(false);
  };
  
  const navigateToNextRecipe = () => {
    if (!selectedRecipe || recipes.length === 0) return;
    
    const currentIndex = recipes.findIndex(r => r.recipe_id === selectedRecipe.recipe_id);
    const nextIndex = (currentIndex + 1) % recipes.length;
    setSelectedRecipe(recipes[nextIndex]);
    setEditMode(false);
  };
  
  const navigateToPrevRecipe = () => {
    if (!selectedRecipe || recipes.length === 0) return;
    
    const currentIndex = recipes.findIndex(r => r.recipe_id === selectedRecipe.recipe_id);
    const prevIndex = (currentIndex - 1 + recipes.length) % recipes.length;
    setSelectedRecipe(recipes[prevIndex]);
    setEditMode(false);
  };

  const changeTierInDetail = async (newTier) => {
    if (!selectedRecipe) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/tier-recipes/${selectedRecipe.recipe_id}?tier=${newTier}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedRecipe(response.data);
    } catch (error) {
      console.error('Error switching tier:', error);
      toast.error('Failed to switch tier');
    }
  };

  const nextTier = () => {
    if (!selectedRecipe) return;
    const currentIndex = selectedRecipe.available_tiers.indexOf(selectedRecipe.current_tier);
    if (currentIndex < selectedRecipe.available_tiers.length - 1) {
      changeTierInDetail(selectedRecipe.available_tiers[currentIndex + 1]);
    }
  };

  const prevTier = () => {
    if (!selectedRecipe) return;
    const currentIndex = selectedRecipe.available_tiers.indexOf(selectedRecipe.current_tier);
    if (currentIndex > 0) {
      changeTierInDetail(selectedRecipe.available_tiers[currentIndex - 1]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.csv')) {
      setCsvFile(file);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setUploading(true);
      setValidationResults(null);
      const formData = new FormData();
      formData.append('file', csvFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tier-recipes/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Show validation results
      const validation = response.data.validation;
      setValidationResults(validation);
      
      if (validation.error_count === 0 && validation.warning_count === 0) {
        toast.success(`✅ Imported ${response.data.imported_count} recipes perfectly!`);
        setImportDialogOpen(false);
        setCsvFile(null);
        fetchRecipes();
      } else {
        toast.warning(`Imported ${response.data.imported_count} recipes with ${validation.warning_count} warnings, ${validation.error_count} errors`);
        // Keep dialog open to show validation results
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import recipes');
    } finally {
      setUploading(false);
    }
  };

  const toggleRecipeSelection = (recipeId) => {
    const newSelected = new Set(selectedRecipeIds);
    if (newSelected.has(recipeId)) {
      newSelected.delete(recipeId);
    } else {
      newSelected.add(recipeId);
    }
    setSelectedRecipeIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedRecipeIds.size === recipes.length) {
      setSelectedRecipeIds(new Set());
    } else {
      setSelectedRecipeIds(new Set(recipes.map(r => r.recipe_id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRecipeIds.size === 0) {
      toast.error('No recipes selected');
      return;
    }

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/tier-recipes/delete`,
        { recipe_ids: Array.from(selectedRecipeIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`✅ Deleted ${response.data.deleted_count} recipe(s)`);
      setSelectedRecipeIds(new Set());
      setDeleteDialogOpen(false);
      fetchRecipes();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete recipes');
    } finally {
      setDeleting(false);
    }
  };
  
  const moveIngredientToCookingEssentials = (foodBankId) => {
    if (!selectedRecipe) return;
    
    // Get current cooking essentials as array
    const currentEssentials = selectedRecipe.cooking_essentials 
      ? selectedRecipe.cooking_essentials.split(',').map(id => id.trim()).filter(id => id)
      : [];
    
    // Add this ingredient if not already there
    if (!currentEssentials.includes(foodBankId)) {
      currentEssentials.push(foodBankId);
      
      // Update selected recipe state
      setSelectedRecipe({
        ...selectedRecipe,
        cooking_essentials: currentEssentials.join(',')
      });
      
      toast.success('Moved to Cooking Essentials (click Save Changes to apply)');
    }
  };
  
  const moveIngredientToMain = (foodBankId) => {
    if (!selectedRecipe) return;
    
    // Get current cooking essentials as array
    const currentEssentials = selectedRecipe.cooking_essentials 
      ? selectedRecipe.cooking_essentials.split(',').map(id => id.trim()).filter(id => id)
      : [];
    
    // Remove this ingredient
    const newEssentials = currentEssentials.filter(id => id !== foodBankId);
    
    // Update selected recipe state
    setSelectedRecipe({
      ...selectedRecipe,
      cooking_essentials: newEssentials.join(',')
    });
    
    toast.success('Moved to Main Ingredients (click Save Changes to apply)');
  };
  
  const saveIngredientChanges = async () => {
    if (!selectedRecipe) return;
    
    try {
      setSavingChanges(true);
      const token = localStorage.getItem('token');
      
      await axios.patch(
        `${API}/tier-recipes/${selectedRecipe.recipe_id}`,
        { cooking_essentials: selectedRecipe.cooking_essentials },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Changes saved! Applied to all tiers (400-1400 kcal)');
      setEditMode(false);
      fetchRecipes(); // Refresh list
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleGenerateImages = async () => {
    // Calculate current page recipes
    const indexOfLastRecipe = currentPage * recipesPerPage;
    const indexOfFirstRecipe = indexOfLastRecipe - recipesPerPage;
    const currentRecipes = recipes.slice(indexOfFirstRecipe, indexOfLastRecipe);
    
    // Filter to only recipes missing images
    const recipesMissingImages = currentRecipes.filter(r => !r.image_url || !r.image_url.startsWith('/api/images'));
    const recipeIds = recipesMissingImages.map(r => r.recipe_id);
    
    if (recipeIds.length === 0) {
      toast.success('✅ All recipes on this page already have images!', { duration: 3000 });
      return;
    }

    try {
      setGeneratingImages(true);
      const token = localStorage.getItem('token');
      
      toast.info(`🎨 Generating images for ${recipeIds.length} recipe${recipeIds.length > 1 ? 's' : ''}...`, {
        duration: 4000
      });
      
      const response = await axios.post(
        `${API}/tier-recipes/generate-images-batch`,
        { recipe_ids: recipeIds },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000 // 5 minutes
        }
      );

      const { generated_count, failed_count, message } = response.data;
      
      if (failed_count > 0) {
        toast.warning(`${message} ${failed_count} failed - try again.`, { duration: 6000 });
      } else {
        toast.success(`✅ ${message}`, { duration: 5000 });
      }
      
      fetchRecipes(); // Refresh to show new images
    } catch (error) {
      console.error('Image generation error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Generation timed out. Try again or reduce batch size.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate images');
      }
    } finally {
      setGeneratingImages(false);
    }
  };
  
  const handleDeleteImage = async (recipeId, recipeName) => {
    try {
      setDeletingImageId(recipeId);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${API}/tier-recipes/${recipeId}/image`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`🗑️ Deleted image for ${recipeName}`);
      fetchRecipes(); // Refresh to show updated state
    } catch (error) {
      console.error('Delete image error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    } finally {
      setDeletingImageId(null);
    }
  };
  
  const handleRegenerateImage = async (recipeId, recipeName) => {
    try {
      setRegeneratingImageId(recipeId);
      const token = localStorage.getItem('token');
      
      toast.info(`🎨 Regenerating image for ${recipeName}...`, { duration: 3000 });
      
      const response = await axios.post(
        `${API}/tier-recipes/${recipeId}/regenerate-image`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // 1 minute
        }
      );
      
      toast.success(`✅ ${response.data.message}`);
      fetchRecipes(); // Refresh to show new image
    } catch (error) {
      console.error('Regenerate image error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Regeneration timed out. Try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to regenerate image');
      }
    } finally {
      setRegeneratingImageId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tier-Based Recipe Library</h1>
            <p className="text-gray-600 mt-2">
              Review all recipes at the same tier, or click on a recipe to view all its tier variations
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            {selectedRecipeIds.size > 0 && (
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedRecipeIds.size})
              </Button>
            )}
            
            <Button
              onClick={() => setImportDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            
            <Button
              onClick={handleGenerateImages}
              disabled={generatingImages || recipes.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generatingImages ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {(() => {
                    const indexOfLastRecipe = currentPage * recipesPerPage;
                    const indexOfFirstRecipe = indexOfLastRecipe - recipesPerPage;
                    const currentRecipes = recipes.slice(indexOfFirstRecipe, indexOfLastRecipe);
                    const missingCount = currentRecipes.filter(r => !r.image_url || !r.image_url.startsWith('/api/images')).length;
                    
                    if (missingCount === 0) {
                      return 'All Have Images ✓';
                    }
                    return `Generate ${missingCount} Missing Image${missingCount > 1 ? 's' : ''}`;
                  })()}
                </>
              )}
            </Button>
            
            <Button
              onClick={() => setCsvFormatDialogOpen(true)}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              CSV Format Help
            </Button>
            
            <Button onClick={() => setListViewOpen(true)} variant="outline">
              List
            </Button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('library')}
            data-testid="tab-library"
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'library'
                ? 'border-[#1a61e8] text-[#1a61e8]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            Recipe Library
          </button>
          <button
            onClick={() => setActiveTab('content')}
            data-testid="tab-content"
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'content'
                ? 'border-[#1a61e8] text-[#1a61e8]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ImagePlus className="h-4 w-4" />
            Content Creator
          </button>
        </div>

        {activeTab === 'content' ? (
          <ContentCreator recipes={recipes} />
        ) : (

        <>
        {/* Selection Controls */}
        {!selectedRecipe && recipes.length > 0 && (
          <div className="mb-4 bg-white p-3 rounded-lg shadow-sm flex items-center gap-4">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {selectedRecipeIds.size === recipes.length ? (
                <CheckSquare className="h-5 w-5 text-blue-600" />
              ) : (
                <Square className="h-5 w-5" />
              )}
              {selectedRecipeIds.size === recipes.length ? 'Deselect All' : 'Select All'}
            </button>
            {selectedRecipeIds.size > 0 && (
              <span className="text-sm text-gray-600">
                {selectedRecipeIds.size} of {recipes.length} selected
              </span>
            )}
          </div>
        )}

        {/* Tier Selector for Library View */}
        {!selectedRecipe && (
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search Input */}
              <div className="flex items-center gap-2 flex-1 min-w-[250px]">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search recipes by name..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="flex-1"
                />
              </div>
              
              {/* Tier Selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Calorie Tier:</label>
                <Select value={selectedTier.toString()} onValueChange={(val) => setSelectedTier(val === 'all' ? 'all' : parseInt(val))}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="200">200 kcal</SelectItem>
                    <SelectItem value="300">300 kcal</SelectItem>
                    <SelectItem value="400">400 kcal</SelectItem>
                    <SelectItem value="500">500 kcal</SelectItem>
                    <SelectItem value="600">600 kcal</SelectItem>
                    <SelectItem value="700">700 kcal</SelectItem>
                    <SelectItem value="800">800 kcal</SelectItem>
                    <SelectItem value="900">900 kcal</SelectItem>
                    <SelectItem value="1000">1000 kcal</SelectItem>
                    <SelectItem value="1100">1100 kcal</SelectItem>
                    <SelectItem value="1200">1200 kcal</SelectItem>
                    <SelectItem value="1300">1300 kcal</SelectItem>
                    <SelectItem value="1400">1400 kcal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Meal Type Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Meal Type:</label>
                <Select value={mealTypeFilter} onValueChange={setMealTypeFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="breakfast">🍳 Breakfast</SelectItem>
                    <SelectItem value="main_meal">🍽️ Main Meal</SelectItem>
                    <SelectItem value="snack">🍿 Snack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Diet Type Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Diet Type:</label>
                <Select value={dietTypeFilter} onValueChange={setDietTypeFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Diets" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Diets</SelectItem>
                    <SelectItem value="standard">🍖 Standard</SelectItem>
                    <SelectItem value="vegetarian">🥗 Vegetarian</SelectItem>
                    <SelectItem value="vegan">🌱 Vegan</SelectItem>
                    <SelectItem value="pescatarian">🐟 Pescatarian</SelectItem>
                    <SelectItem value="keto">🥑 Keto</SelectItem>
                    <SelectItem value="paleo">🦴 Paleo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Clear Filters & Recipe Count */}
              <div className="flex items-center gap-2 ml-auto">
                {(mealTypeFilter !== 'all' || dietTypeFilter !== 'all' || selectedTier !== 'all' || searchInput.trim()) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMealTypeFilter('all');
                      setDietTypeFilter('all');
                      setSelectedTier('all');
                      setSearchInput('');
                    }}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    Clear Filters
                  </Button>
                )}
                <span className="text-sm text-gray-500">
                  ({recipes.length} recipe{recipes.length !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Recipe Detail View */}
        {selectedRecipe && (
          <div className="mb-6 bg-white p-6 rounded-lg shadow-lg relative">
            {/* Navigation Arrows */}
            <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
              <button
                onClick={navigateToPrevRecipe}
                className="bg-white hover:bg-gray-50 p-2 rounded-full shadow-md border border-gray-200"
                title="Previous recipe (← Arrow Key)"
              >
                <ChevronLeft className="h-5 w-5 text-gray-700" />
              </button>
            </div>
            
            <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
              <button
                onClick={navigateToNextRecipe}
                className="bg-white hover:bg-gray-50 p-2 rounded-full shadow-md border border-gray-200"
                title="Next recipe (→ Arrow Key)"
              >
                <ChevronRight className="h-5 w-5 text-gray-700" />
              </button>
            </div>
            
            {/* Recipe Image */}
            {selectedRecipe.image_url && (
              <div className="mb-6 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center w-full max-w-sm mx-auto aspect-square">
                <img 
                  src={selectedRecipe.image_url} 
                  alt={selectedRecipe.name}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            <div className="flex justify-between items-start mb-6">
              <div>
                <Button 
                  variant="ghost" 
                  onClick={closeRecipeDetail}
                  className="mb-4"
                >
                  ← Back to Library
                </Button>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedRecipe.name}</h2>
                  <span className="text-sm text-gray-500">
                    ({recipes.findIndex(r => r.recipe_id === selectedRecipe.recipe_id) + 1} of {recipes.length})
                  </span>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Badge variant="secondary">{selectedRecipe.meal_type}</Badge>
                  <Badge variant="outline">{selectedRecipe.diet_type}</Badge>
                  <Badge>{selectedRecipe.cooking_time}</Badge>
                  {selectedRecipe.halal === false && (
                    <Badge className="bg-red-100 text-red-800">🚫 Non-Halal</Badge>
                  )}
                </div>
                {/* Allergens in detail view */}
                {selectedRecipe.allergens && selectedRecipe.allergens.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-semibold text-red-900 mb-2">⚠️ Contains Allergens:</p>
                    <div className="flex gap-2 flex-wrap">
                      {selectedRecipe.allergens.map(allergen => (
                        <Badge key={allergen} variant="destructive" className="text-xs">
                          {allergen}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Edit Mode Toggle */}
              <div className="flex gap-2">
                {editMode && (
                  <Button
                    onClick={saveIngredientChanges}
                    disabled={savingChanges}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {savingChanges ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                )}
                <Button
                  onClick={() => setEditMode(!editMode)}
                  variant={editMode ? 'destructive' : 'outline'}
                  className={editMode ? '' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}
                >
                  {editMode ? 'Cancel Edit' : 'Edit Ingredients'}
                </Button>
              </div>
            </div>

            {/* Tier Navigator */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevTier}
                  disabled={selectedRecipe.available_tiers.indexOf(selectedRecipe.current_tier) === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous Tier
                </Button>

                <div className="flex flex-col items-center">
                  <span className="text-sm text-gray-600 mb-2">Current Tier</span>
                  <div className="flex items-center gap-2">
                    <Flame className="h-5 w-5 text-orange-500" />
                    <span className="text-3xl font-bold text-gray-900">
                      {selectedRecipe.current_tier}
                    </span>
                    <span className="text-lg text-gray-600">kcal</span>
                  </div>
                  {selectedRecipe.current_tier === selectedRecipe.default_tier && (
                    <Badge variant="secondary" className="mt-2">Default Tier</Badge>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={nextTier}
                  disabled={selectedRecipe.available_tiers.indexOf(selectedRecipe.current_tier) === selectedRecipe.available_tiers.length - 1}
                >
                  Next Tier
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {/* Tier Dots */}
              <div className="flex justify-center gap-1 mt-4">
                {selectedRecipe.available_tiers.map((tier) => (
                  <button
                    key={tier}
                    onClick={() => changeTierInDetail(tier)}
                    className={`h-2 rounded-full transition-all ${
                      tier === selectedRecipe.current_tier
                        ? 'w-8 bg-blue-600'
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    title={`${tier} kcal`}
                  />
                ))}
              </div>
            </div>

            {/* Nutrition Totals */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-orange-50 p-4 rounded-lg text-center">
                <Flame className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{selectedRecipe.totals.calories}</div>
                <div className="text-sm text-gray-600">Calories</div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg text-center">
                <Activity className="h-6 w-6 text-red-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{selectedRecipe.totals.protein}g</div>
                <div className="text-sm text-gray-600">Protein</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg text-center">
                <Wheat className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{selectedRecipe.totals.carbs}g</div>
                <div className="text-sm text-gray-600">Carbs</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <Droplet className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{selectedRecipe.totals.fat}g</div>
                <div className="text-sm text-gray-600">Fat</div>
              </div>
            </div>

            {/* Ingredients */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Ingredients</h3>
                {editMode && (
                  <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Changes apply to all tiers automatically</span>
                  </div>
                )}
              </div>
              
              {(() => {
                // Group ingredients by cooking essentials vs main
                // Handle both comma and pipe separators for flexibility
                const essentialsRaw = selectedRecipe.cooking_essentials || selectedRecipe.seasoning_ids || '';
                const essentialIds = essentialsRaw
                  .split(/[,|]/)  // Split by comma OR pipe
                  .map(id => id.trim())
                  .filter(id => id);
                
                const mainIngredients = selectedRecipe.ingredients.filter(
                  ing => !essentialIds.includes(ing.food_bank_id)
                );
                const cookingEssentials = selectedRecipe.ingredients.filter(
                  ing => essentialIds.includes(ing.food_bank_id)
                );
                
                return (
                  <>
                    {/* Main Ingredients */}
                    {mainIngredients.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <span className="w-1 h-5 bg-blue-500 rounded"></span>
                          Main Ingredients
                        </h4>
                        <ul className="space-y-2.5 ml-3">
                          {mainIngredients.map((ing, idx) => (
                            <li key={idx} className="flex items-start justify-between gap-3 text-gray-700 hover:bg-white p-2 rounded group">
                              <div className="flex items-start gap-3 flex-1">
                                <span className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                                <div className="flex-1">
                                  <span className="font-semibold text-gray-900">{ing.display_text}</span>
                                  <span className="ml-2">{ing.food_item_name}</span>
                                  <span className="ml-2 text-sm text-gray-500">({Math.round(ing.kcal)} kcal)</span>
                                </div>
                              </div>
                              {editMode && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => moveIngredientToCookingEssentials(ing.food_bank_id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-xs h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 flex-shrink-0"
                                >
                                  → Essentials
                                </Button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Cooking Essentials */}
                    {cookingEssentials.length > 0 && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200">
                        <h4 className="text-base font-semibold text-amber-900 mb-3 flex items-center gap-2">
                          <span className="text-xl">🧂</span> Cooking Essentials
                        </h4>
                        <ul className="space-y-2 ml-3">
                          {cookingEssentials.map((ing, idx) => (
                            <li key={idx} className="flex items-center justify-between gap-2 text-amber-900 hover:bg-amber-100 p-2 rounded group">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0"></span>
                                <span className="font-medium">{ing.display_text}</span>
                                <span>{ing.food_item_name}</span>
                              </div>
                              {editMode && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => moveIngredientToMain(ing.food_bank_id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-xs h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex-shrink-0"
                                >
                                  ← Main
                                </Button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Instructions */}
            <div className="mt-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-blue-400">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center text-2xl shadow-md">
                    👨‍🍳
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Cooking Instructions</h3>
                </div>
                
                <div className="space-y-4">
                  {selectedRecipe.instructions ? (
                    (() => {
                      // Split by numbered pattern (e.g., "1. ", "2. ") or newlines
                      const steps = selectedRecipe.instructions
                        .split(/\d+\.\s+/)
                        .filter(step => step.trim());
                      
                      return steps.map((step, idx) => (
                        <div key={idx} className="flex gap-4 items-start group hover:bg-white/60 p-4 rounded-xl transition-all">
                          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md group-hover:scale-110 transition-transform">
                            {idx + 1}
                          </div>
                          <p className="text-gray-800 leading-relaxed pt-0.5 flex-1">
                            {step.trim()}
                          </p>
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-400 italic">No instructions provided</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recipe Grid (Library View) */}
        {!selectedRecipe && (
          <>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading recipes...</p>
              </div>
            ) : recipes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-600">No recipes found at this tier.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Pagination Controls - Top */}
                <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-sm text-gray-600">
                    Showing {Math.min((currentPage - 1) * recipesPerPage + 1, recipes.length)} - {Math.min(currentPage * recipesPerPage, recipes.length)} of {recipes.length} recipes
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm font-medium text-gray-700">
                      Page {currentPage} of {Math.ceil(recipes.length / recipesPerPage)}
                    </span>
                    <Button
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(recipes.length / recipesPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil(recipes.length / recipesPerPage)}
                      variant="outline"
                      size="sm"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
                
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(() => {
                  const indexOfLastRecipe = currentPage * recipesPerPage;
                  const indexOfFirstRecipe = indexOfLastRecipe - recipesPerPage;
                  const currentRecipes = recipes.slice(indexOfFirstRecipe, indexOfLastRecipe);
                  
                  return currentRecipes.map((recipe) => (
                  <Card 
                    key={recipe.recipe_id} 
                    className="hover:shadow-lg transition-shadow overflow-hidden relative"
                  >
                    {/* Selection Checkbox */}
                    <div className="absolute top-3 left-3 z-10">
                      <Checkbox
                        checked={selectedRecipeIds.has(recipe.recipe_id)}
                        onCheckedChange={() => toggleRecipeSelection(recipe.recipe_id)}
                        className="h-5 w-5 bg-white border-2"
                      />
                    </div>
                    
                    <div onClick={() => openRecipeDetail(recipe)} className="cursor-pointer">
                      {recipe.image_url ? (
                        <div className="relative h-48 overflow-hidden bg-gray-100 flex items-center justify-center group">
                          <img 
                            src={recipe.image_url} 
                            alt={recipe.name}
                            className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                          />
                          {/* Image Action Buttons */}
                          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-8 px-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteImage(recipe.recipe_id, recipe.name);
                              }}
                              disabled={deletingImageId === recipe.recipe_id}
                            >
                              {deletingImageId === recipe.recipe_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              className="h-8 px-2 bg-purple-600 hover:bg-purple-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRegenerateImage(recipe.recipe_id, recipe.name);
                              }}
                              disabled={regeneratingImageId === recipe.recipe_id}
                            >
                              {regeneratingImageId === recipe.recipe_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="h-48 bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-400 text-sm">No image</span>
                        </div>
                      )}
                      <CardHeader>
                        <CardTitle className="text-lg">{recipe.name}</CardTitle>
                        <div className="flex gap-2 flex-wrap mt-2">
                          <Badge variant="secondary" className="text-xs">{recipe.meal_type}</Badge>
                          <Badge variant="outline" className="text-xs">{recipe.diet_type}</Badge>
                          {recipe.current_tier === recipe.default_tier && (
                            <Badge className="text-xs bg-green-100 text-green-800">Default</Badge>
                          )}
                          {recipe.halal === false && (
                            <Badge className="text-xs bg-red-100 text-red-800">🚫 Non-Halal</Badge>
                          )}
                        </div>
                        {/* Allergens */}
                        {recipe.allergens && recipe.allergens.length > 0 && (
                          <div className="flex gap-1 flex-wrap mt-2">
                            {recipe.allergens.map(allergen => (
                              <Badge key={allergen} variant="destructive" className="text-xs">
                                ⚠️ {allergen}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Nutrition Summary */}
                        <div className="flex justify-between text-sm">
                          <div className="flex items-center gap-1">
                            <Flame className="h-4 w-4 text-orange-500" />
                            <span className="font-semibold">{recipe.totals.calories}</span>
                            <span className="text-gray-500">kcal</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Activity className="h-4 w-4 text-red-500" />
                            <span className="font-semibold">{recipe.totals.protein}g</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Wheat className="h-4 w-4 text-yellow-500" />
                            <span className="font-semibold">{recipe.totals.carbs}g</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Droplet className="h-4 w-4 text-blue-500" />
                            <span className="font-semibold">{recipe.totals.fat}g</span>
                          </div>
                        </div>

                        {/* Ingredient Count */}
                        <div className="text-sm text-gray-600">
                          {recipe.ingredients.length} ingredients • {recipe.cooking_time}
                        </div>

                        {/* Available Tiers Indicator */}
                        <div className="text-xs text-gray-500">
                          {recipe.available_tiers.length} tiers available 
                          ({recipe.available_tiers[0]}-{recipe.available_tiers[recipe.available_tiers.length - 1]} kcal)
                        </div>
                      </div>
                    </CardContent>
                  </div>
                  </Card>
                  ));
                })()}
              </div>
              
              {/* Pagination Controls - Bottom */}
              <div className="mt-6 flex items-center justify-center bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm font-medium text-gray-700 px-4">
                    Page {currentPage} of {Math.ceil(recipes.length / recipesPerPage)}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(recipes.length / recipesPerPage), prev + 1))}
                    disabled={currentPage >= Math.ceil(recipes.length / recipesPerPage)}
                    variant="outline"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
          </>
          )}
          </>
          )}
        </>
        )}
      </div>
      


      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Selected Recipes</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedRecipeIds.size} recipe(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-3 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) {
          setValidationResults(null);
          setCsvFile(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Recipes from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with tier-based recipes. Must include columns: recipe_id, name, meal_type, diet_type, default_tier, cooking_essentials, and ingredients_400 through ingredients_1400.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {csvFile && (
                <p className="text-sm text-gray-600">
                  Selected: {csvFile.name}
                </p>
              )}
            </div>
            
            {/* Validation Results */}
            {validationResults && (
              <div className="space-y-3 mt-4">
                {validationResults.error_count === 0 && validationResults.warning_count === 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900">✅ All recipes validated successfully!</h4>
                  </div>
                ) : (
                  <>
                    {validationResults.warning_count > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-900 mb-2">
                          ⚠️ {validationResults.warning_count} Warning{validationResults.warning_count > 1 ? 's' : ''}
                        </h4>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {validationResults.warnings.slice(0, 10).map((warning, idx) => (
                            <p key={idx} className="text-sm text-yellow-800">
                              • {warning.recipe_name}: {warning.message}
                            </p>
                          ))}
                          {validationResults.warnings.length > 10 && (
                            <p className="text-sm text-yellow-700 italic">
                              ...and {validationResults.warnings.length - 10} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {validationResults.error_count > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-2">
                          ❌ {validationResults.error_count} Error{validationResults.error_count > 1 ? 's' : ''}
                        </h4>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {validationResults.errors.slice(0, 10).map((error, idx) => (
                            <p key={idx} className="text-sm text-red-800">
                              • {error.recipe_name}: {error.message}
                            </p>
                          ))}
                          {validationResults.errors.length > 10 && (
                            <p className="text-sm text-red-700 italic">
                              ...and {validationResults.errors.length - 10} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setImportDialogOpen(false);
                  setValidationResults(null);
                  setCsvFile(null);
                }}
                disabled={uploading}
              >
                {validationResults ? 'Close' : 'Cancel'}
              </Button>
              {!validationResults && (
                <Button
                  onClick={handleImportCSV}
                  disabled={!csvFile || uploading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* CSV Format Helper Dialog */}
      <Dialog open={csvFormatDialogOpen} onOpenChange={setCsvFormatDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📋 CSV Format Reference</DialogTitle>
            <DialogDescription>
              Copy-paste friendly format for creating recipe CSVs. Use this as a template or reference.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* CSV Header */}
            <div>
              <h3 className="font-semibold text-sm mb-2 text-gray-700">CSV Column Headers (Tab-Separated):</h3>
              <div className="bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto">
                <pre className="text-xs font-mono whitespace-pre">
recipe_id	name	meal_type	diet_type	dish_category	image_prompt	cooking_time	high_protein	spice_level	equipment_needed	budget	cuisine_type	instructions_steps	allergens	restrictions	default_tier	serving_size	cooking_difficulty	meal_prep_suitable	cooking_essentials	ingredients_400	ingredients_500	ingredients_600	ingredients_700	ingredients_800	ingredients_900	ingredients_1000	ingredients_1100	ingredients_1200	ingredients_1300	ingredients_1400
                </pre>
              </div>
            </div>
            
            {/* Valid Options */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-gray-700">Valid Options for Each Column:</h3>
              
              <div className="grid grid-cols-1 gap-3 text-xs">
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">recipe_id:</span>
                  <span className="text-blue-700 ml-2">Unique identifier (e.g., BF_001, LN_045, DIN_123)</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">name:</span>
                  <span className="text-blue-700 ml-2">Recipe name (any text)</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <span className="font-semibold text-green-900">meal_type:</span>
                  <span className="text-green-700 ml-2">breakfast, main_meal, snack</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <span className="font-semibold text-green-900">diet_type:</span>
                  <span className="text-green-700 ml-2">standard, vegetarian, vegan, pescatarian, keto, paleo</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <span className="font-semibold text-green-900">dish_category:</span>
                  <span className="text-green-700 ml-2">egg_dishes, pancakes_waffles, yogurt_bowls, oatmeal_porridge, smoothie_bowls, avocado_toast, breakfast_burrito, grain_bowls, salads, wraps_sandwiches, pasta, stir_fry, curry, soup_stew, rice_bowls, tacos, pizza, fish_seafood, chicken_dishes, beef_dishes, vegetarian_mains, one_pot_meals</span>
                </div>
                
                <div className="bg-purple-50 p-3 rounded border border-purple-200">
                  <span className="font-semibold text-purple-900">image_prompt:</span>
                  <span className="text-purple-700 ml-2">Text description for AI image generation (e.g., "Overhead view of fluffy pancakes with maple syrup on white plate")</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">cooking_time:</span>
                  <span className="text-blue-700 ml-2">Text format (e.g., "15 min", "30 min", "1 hour")</span>
                </div>
                
                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                  <span className="font-semibold text-amber-900">high_protein:</span>
                  <span className="text-amber-700 ml-2">true, false</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <span className="font-semibold text-green-900">spice_level:</span>
                  <span className="text-green-700 ml-2">none, mild, medium, spicy, very_spicy</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">equipment_needed:</span>
                  <span className="text-blue-700 ml-2">Pipe-separated list (e.g., "pan|spatula|bowl")</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <span className="font-semibold text-green-900">budget:</span>
                  <span className="text-green-700 ml-2">budget, affordable, moderate, premium</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <span className="font-semibold text-green-900">cuisine_type:</span>
                  <span className="text-green-700 ml-2">british, american, italian, mexican, asian, indian, mediterranean, middle_eastern, french, spanish, thai, japanese, chinese</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">instructions_steps:</span>
                  <span className="text-blue-700 ml-2">Numbered steps (e.g., "1. Preheat oven. 2. Mix ingredients. 3. Bake for 20 minutes.")</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">allergens:</span>
                  <span className="text-blue-700 ml-2">Comma or pipe-separated (e.g., "egg,dairy,gluten")</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">restrictions:</span>
                  <span className="text-blue-700 ml-2">Comma or pipe-separated dietary restrictions</span>
                </div>
                
                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                  <span className="font-semibold text-amber-900">default_tier:</span>
                  <span className="text-amber-700 ml-2">Recommended calorie level (400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400)</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <span className="font-semibold text-blue-900">serving_size:</span>
                  <span className="text-blue-700 ml-2">Text (e.g., "1 serving", "2 servings")</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <span className="font-semibold text-green-900">cooking_difficulty:</span>
                  <span className="text-green-700 ml-2">easy, medium, hard</span>
                </div>
                
                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                  <span className="font-semibold text-amber-900">meal_prep_suitable:</span>
                  <span className="text-amber-700 ml-2">true, false</span>
                </div>
                
                <div className="bg-purple-50 p-3 rounded border border-purple-200">
                  <span className="font-semibold text-purple-900">cooking_essentials:</span>
                  <span className="text-purple-700 ml-2">Pipe or comma-separated Food Bank IDs for salt, pepper, oil, etc. (e.g., "163|186|187")</span>
                </div>
                
                <div className="bg-red-50 p-3 rounded border border-red-200">
                  <span className="font-semibold text-red-900">ingredients_400 to ingredients_1400:</span>
                  <span className="text-red-700 ml-2">Pipe-separated FoodID:Grams pairs for each calorie tier (e.g., "39:100|40:50|163:14|106:80")</span>
                  <div className="mt-1 text-red-600 text-xs">
                    Format: <code className="bg-red-100 px-1 rounded">FoodBankID:GramsAmount|FoodBankID:GramsAmount|...</code>
                  </div>
                  <div className="mt-1 text-red-600 text-xs">
                    Example: <code className="bg-red-100 px-1 rounded">204:3|163:14|39:100</code> = 3g Baking Powder + 14g Olive Oil + 100g Eggs
                  </div>
                </div>
              </div>
            </div>
            
            {/* Example Row */}
            <div>
              <h3 className="font-semibold text-sm mb-2 text-gray-700">Example CSV Row:</h3>
              <div className="bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto">
                <pre className="text-xs font-mono whitespace-pre">
BF_001	Mushroom Scramble	breakfast	vegetarian	egg_dishes	Overhead photo of fluffy scrambled eggs with mushrooms on white plate	15 min	true	mild	pan|spatula	affordable	british	1. Heat oil in pan. 2. Add mushrooms and cook 5 min. 3. Add eggs and scramble.	egg	none	600	1 serving	easy	true	163|186|187	39:80|40:20|163:7|80:60	39:100|40:30|163:10|80:80	39:120|40:40|163:14|80:100	...
                </pre>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs">
              <p className="font-semibold text-blue-900 mb-1">💡 Tips:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Get Food Bank IDs from: Coach Dashboard → Food Bank (each item has an ID)</li>
                <li>Grams are stored in database; display units come from Food Bank settings</li>
                <li>Use tabs (	) or commas (,) as separators in CSV</li>
                <li>Image generation uses the image_prompt field</li>
                <li>Cooking essentials IDs are filtered from main ingredient list automatically</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipe List View Modal */}
      <Dialog open={listViewOpen} onOpenChange={setListViewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recipe List ({recipes.length} recipes)</DialogTitle>
            <DialogDescription>
              Select and copy recipe names
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-gray-50 rounded-md font-mono text-sm whitespace-pre-wrap">
            {recipes.map((recipe, idx) => (
              <div key={idx}>{recipe.name}</div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
